'use client';

import { useSession, signOut } from 'next-auth/react';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

export function UserMenu() {
  const { data: session, status } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    signOut({ callbackUrl: '/' });
  };

  if (status === 'loading') {
    return (
      <div style={styles.skeleton}>
        <div style={styles.skeletonAvatar}></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={styles.authLinks}>
        <Link href="/auth/signin" style={styles.signInLink}>
          Sign In
        </Link>
        <Link href="/auth/signup" style={styles.signUpLink}>
          Sign Up
        </Link>
      </div>
    );
  }

  const initials = session.user?.name
    ? session.user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
    : session.user?.email?.[0].toUpperCase() || '?';

  return (
    <div ref={menuRef} style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={styles.avatarButton}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        {session.user?.image ? (
          <Image
            src={session.user.image}
            alt={session.user.name || 'User avatar'}
            style={styles.avatarImage}
            width={36}
            height={36}
            objectFit="cover"
          />
        ) : (
          <div style={styles.avatarInitials}>{initials}</div>
        )}
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{session.user?.name || 'User'}</div>
            <div style={styles.userEmail}>{session.user?.email}</div>
          </div>
          <div style={styles.divider}></div>
          <button onClick={handleSignOut} style={styles.signOutButton}>
            <svg
              style={styles.signOutIcon}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
  },
  skeleton: {
    display: 'flex',
    alignItems: 'center',
  },
  skeletonAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  authLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  signInLink: {
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    fontSize: '0.9rem',
    padding: '0.5rem 1rem',
    borderRadius: 6,
    transition: 'color 0.2s',
  },
  signUpLink: {
    background: '#2d6cdf',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '0.9rem',
    padding: '0.5rem 1rem',
    borderRadius: 6,
    fontWeight: 500,
    transition: 'background 0.2s',
  },
  avatarButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    borderRadius: '50%',
    overflow: 'hidden',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarImage: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover' as const,
  },
  avatarInitials: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#2d6cdf',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  dropdown: {
    position: 'absolute' as const,
    top: 'calc(100% + 8px)',
    right: 0,
    background: '#1e293b',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    minWidth: 220,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    overflow: 'hidden',
  },
  userInfo: {
    padding: '1rem',
  },
  userName: {
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.95rem',
    marginBottom: 2,
  },
  userEmail: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  divider: {
    height: 1,
    background: 'rgba(255, 255, 255, 0.1)',
  },
  signOutButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
    padding: '0.875rem 1rem',
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background 0.2s',
    textAlign: 'left' as const,
  },
  signOutIcon: {
    width: 18,
    height: 18,
  },
};
